/**
 * FL-2 — finishPolicy pure resolver (omo A-7)
 *
 * finish 호출 시 결정 로직을 pure function으로 단일화.
 *   - 입력: 모두 plain serializable (buffer state, raw assistant text, retry counter, session flags, user input classification, injectable detectors)
 *   - 출력: { action, reason, retryGuide?, transforms? }
 *   - I/O / side effect 없음 → useAiChat (production) + nodeAgent (test) 양쪽 동일 호출 가능
 *   - dual sync class 자체 발생 X
 *
 * 책임 — 3 guard 순차 평가:
 *   1. DISPLAY REQUIRED — !finishAllowedByGuard + items 있음 + list intent + !count/!meta → retry
 *   2. OUTRO RAW LEAK — !outroRetryAttempted + items 있음 + detector "detected" → retry (1회 한정)
 *   3. NUMERIC HALLUCINATION (B1) — numericHallucinationRetryCount === 0 + items 있음 + detector "detected" → retry (1회 한정)
 *   4. 모두 통과 → finish + transforms hint
 *
 * 비책임 (caller가 처리):
 *   - 텍스트 transform (stripPoliteReproposal / substituteOutroPlaceholders / augmentOutroWithChange / stripRawNumberArtifacts)
 *     → action === 'finish' 시 transforms 배열을 순차 적용
 *   - POST-GUARD non-finish tool 차단 → 별도 Continuation hook (FL-1)
 *   - 렌더링 / 메시지 push / log push → 외부 통합 layer
 *
 * Detector는 inject — 단위 테스트는 mock, integration은 aiUtils의 실제 함수 inject.
 *
 * 참조: src/AiManager/Test/refactor-omo-adoption.md §3.1 FL-2
 */

export const FINISH_REASONS = Object.freeze({
  DISPLAY_REQUIRED: 'display_required',
  OUTRO_RAW_LEAK: 'outro_raw_leak',
  NUMERIC_HALLUCINATION: 'numeric_hallucination',
  NO_DATA_FETCH: 'no_data_fetch',
  DETAIL_FALLBACK_MISLEADING: 'detail_fallback_misleading',  // [anchor 16]
  EMPTY_RESPONSE_AFTER_VIZ: 'empty_response_after_viz',      // [1-F]
  KPI_VALUE_MISSING: 'kpi_value_missing',                    // [1-A γ]
  ZERO_COUNT_ALTERNATIVE_UNTRIED: 'zero_count_alternative_untried',  // [1-E γ]
  PERIOD_COUNTS_MISSING: 'period_counts_missing',            // [1-F γ]
  PERIOD_COUNT_MISMATCH: 'period_count_mismatch',            // [1-N P26]
  ENTITY_DETAIL_MISSING: 'entity_detail_missing',            // [1-K GUARD 10]
  COLUMN_ENUM_LEAK: 'column_enum_leak',
  EMPTY_BUFFER_COUNT_HALLUCINATION: 'empty_buffer_count_hallucination',  // [L30-CONCEPT-006] 2026-05-31
  BUFFER_MISMATCH_UNRESOLVED: 'buffer_mismatch_unresolved',  // [C-#6 카테고리전환 버퍼오염] 2026-06-24
  OK: 'ok',
});

// [1-F] 시각화 도구 후 빈 response 차단용 — show_display는 자체 표 렌더링이라 제외 (응답 텍스트 비어도 표시됨)
const VIZ_TOOLS_REQUIRE_RESPONSE = new Set([
  'show_sales_view', 'show_revenue_chart',
  'show_attendance_chart', 'show_attendance_ranking',
  'show_class_attendance', 'show_member_status',
  'show_ltv', 'show_engagement_chart',
]);

export const FINISH_TRANSFORMS = Object.freeze({
  SANITIZE_POLITE: 'sanitizePolite',
  PLACEHOLDER_SUBSTITUTE: 'placeholderSubstitute',
  AUGMENT_CHANGE: 'augmentChange',
  STRIP_RAW_NUMBER_POST_RETRY: 'stripRawNumberPostRetry',
});

const ITEM_KEY_HINT_LIMIT = 12;

const _RAW_CAMEL_RE = /^[a-zA-Z]/;

// ────────────────────────────────────────
// Retry guide 노출용 raw value → 사용자 친화 value 변환
//   본질: ENTITY_DETAIL_MISSING retry guide가 buffer raw value(gender=male, phone=01089277152, birthDay=19911210)를
//   그대로 LLM에 prompt로 노출 시 retry 응답에서 raw value 재현 (L10-DETAIL-SALES-001).
//   해소: retry guide에 표시되는 value를 한국어/포맷 변환된 형태로 전달 → LLM이 변환된 값 그대로 인용.
//   범용: field name 기반 변환 (gender/phone/birthDay/profile 등 detail intention 공통 raw field).
//   case-ID/specific 입력 string 무참조. enum 매핑은 등록된 field만 변환 (single input string match 미사용).
// ────────────────────────────────────────
const _GENDER_ENUM_MAP = { male: '남성', female: '여성', M: '남', F: '여' };

function _formatPhoneRaw(value) {
  if (typeof value !== 'string') return null;
  // 11자리 010XXXXXXXX → 010-XXXX-XXXX (그 외 unrecognized → null)
  if (/^010\d{8}$/.test(value)) {
    return `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7)}`;
  }
  // 10자리 02XXXXXXXX 등도 정상 지원
  if (/^02\d{7,8}$/.test(value)) {
    const mid = value.length === 9 ? value.slice(2, 5) : value.slice(2, 6);
    return `02-${mid}-${value.slice(-4)}`;
  }
  return null;
}

function _formatBirthDayRaw(value) {
  if (typeof value !== 'string') return null;
  // 19911210 → 1991-12-10
  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6)}`;
  }
  return null;
}

// detail field value를 사용자 친화 표현으로 변환. 변환 불가 시 raw 그대로.
//   - gender: male/female/M/F → 한국어
//   - phone/tel/mobile/phoneNumber/contactNumber: 11자리 raw → 010-XXXX-XXXX
//   - birthDay/birthday/birthDate: 8자리 raw → YYYY-MM-DD
//   - profile/coachProfile: http(s) URL → "등록됨" (raw URL 노출 방지)
export function _formatRetryGuideValue(field, value) {
  if (value == null) return value;
  const sv = String(value);
  // gender enum
  if (field === 'gender' && _GENDER_ENUM_MAP[sv] !== undefined) {
    return _GENDER_ENUM_MAP[sv];
  }
  // phone 패밀리
  if (/^(phone|tel|mobile|phoneNumber|contactNumber)$/i.test(field)) {
    const formatted = _formatPhoneRaw(sv);
    if (formatted) return formatted;
  }
  // birthDay 패밀리
  if (/^(birthDay|birthday|birthDate)$/i.test(field)) {
    const formatted = _formatBirthDayRaw(sv);
    if (formatted) return formatted;
  }
  // profile URL
  if (/^(profile|coachProfile)$/i.test(field) && /^https?:\/\//i.test(sv)) {
    return '등록됨';
  }
  return sv;
}

export function detectColumnEnumLeak(text, buffer) {
  if (!text || !buffer?.resolvedColumnLabels) return { detected: false };
  const labels = Object.values(buffer.resolvedColumnLabels)
    .filter(l => typeof l === 'string' && l.length >= 2 && !_RAW_CAMEL_RE.test(l));
  if (labels.length < 3) return { detected: false };
  const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const sep = '(?:\\s*[,、·/]\\s*|\\s*(?:과|와|이나|하고|및)\\s*)';
  const re = new RegExp(
    `(?:${labels.map(esc).join('|')})(?:${sep}(?:${labels.map(esc).join('|')})){2,}`
  );
  const m = text.match(re);
  return m ? { detected: true, sample: m[0].slice(0, 80) } : { detected: false };
}

/**
 * @param {FinishPolicyInput} input
 * @returns {FinishDecision}
 */
export function evaluateFinish(input = {}) {
  const {
    buffer = null,
    rawAssistantText = '',
    retryCount = {},
    sessionFlags = {},
    userInputClassification = {},
    detectors = {},
  } = input;

  const finishAllowedByGuard = !!sessionFlags.finishAllowedByGuard;
  const {
    isListIntent = false,
    isCountIntent = false,
    isMetaIntent = false,
  } = userInputClassification;
  const outroRetryAttempted = !!retryCount.outroRetryAttempted;
  const numericHallucinationRetryCount = retryCount.numericHallucinationRetryCount ?? 0;
  const noDataFetchRetryAttempted = !!retryCount.noDataFetchRetryAttempted;
  const detailFallbackRetryAttempted = !!retryCount.detailFallbackRetryAttempted;  // [anchor 16]
  const emptyResponseAfterVizRetryAttempted = !!retryCount.emptyResponseAfterVizRetryAttempted;  // [1-F]
  const kpiValueMissingRetryAttempted = !!retryCount.kpiValueMissingRetryAttempted;  // [1-A γ]
  const zeroCountAlternativeRetryAttempted = !!retryCount.zeroCountAlternativeRetryAttempted;  // [1-E γ]
  const periodCountsMissingRetryAttempted = !!retryCount.periodCountsMissingRetryAttempted;  // [1-F γ]
  const periodCountMismatchRetryAttempted = !!retryCount.periodCountMismatchRetryAttempted;  // [1-N P26]
  const entityDetailMissingRetryAttempted = !!retryCount.entityDetailMissingRetryAttempted;  // [1-K GUARD 10]
  const columnEnumRetryAttempted = !!retryCount.columnEnumRetryAttempted;
  const bufferMismatchRetryAttempted = !!retryCount.bufferMismatchRetryAttempted;  // [C-#6] 2026-06-24
  const bufferMismatchUnresolved = !!sessionFlags.bufferMismatchUnresolved;          // [C-#6] 이번 turn BUFFER MISMATCH 발생 + 성공 표출 없음
  const bufferMismatchInfo = input.bufferMismatchInfo || null;                       // [C-#6] {columns, activeSlot}
  const toolsCalledThisTurn = Array.isArray(input.toolsCalledThisTurn) ? input.toolsCalledThisTurn : [];
  const detailNotFoundContext = sessionFlags.detailNotFoundContext || null;  // [anchor 16]
  const kpiHintMeta = input.kpiHintMeta || null;  // [1-A γ] {slotName, intention, summary} — show_display에서 KPI buffer 감지 시 set
  const zeroCountAlternativeMeta = input.zeroCountAlternativeMeta || null;  // [1-E γ] {intention, count, alternatives[]} — call_api 2+ count=0 동일 intention 감지 시 set
  const periodCountHintMeta = input.periodCountHintMeta || null;  // [1-F γ] {perPeriod: [{periodLabel, count, source, ...}], totalSlots} — merge_slots multi-period distinct dateRange 감지 시 set
  const periodCountMismatchMeta = input.periodCountMismatchMeta || null;  // [1-N P26] {inputMonths, calledMonths, missingMonths, sourceIntention} — user input month count > call_api month count 감지 시 set

  const itemsAvailable = Array.isArray(buffer?.items) && buffer.items.length > 0;
  const bufferEmpty = !itemsAvailable;

  // ────────────────────────────────────────
  // GUARD 6 — EMPTY RESPONSE AFTER VIZ ([1-F], 1회 한정)
  //   조건: 이번 turn에 시각화 도구 (show_*chart/ranking/view/status/ltv) 호출 + finish response가 빈 값 + 1회 미시도
  //   본질: LLM이 시각화 도구 호출 후 finish() 시 response argument를 빈 값으로 보냄 → finalText 비어버림
  //   show_display는 표 자체 렌더링되므로 제외 (응답 텍스트 비어도 표시됨)
  // ────────────────────────────────────────
  if (!emptyResponseAfterVizRetryAttempted) {
    const trimmed = typeof rawAssistantText === 'string' ? rawAssistantText.trim() : '';
    if (trimmed.length === 0) {
      const vizCalled = toolsCalledThisTurn.find(t => VIZ_TOOLS_REQUIRE_RESPONSE.has(t));
      if (vizCalled) {
        return {
          action: 'retry',
          reason: FINISH_REASONS.EMPTY_RESPONSE_AFTER_VIZ,
          retryGuide: { vizTool: vizCalled },
        };
      }
    }
  }

  // ────────────────────────────────────────
  // GUARD 14 — BUFFER MISMATCH UNRESOLVED ([C-#6 카테고리전환 버퍼오염] 2026-06-24, 1회 한정)
  // ────────────────────────────────────────
  //   조건: 이번 turn show_display가 BUFFER MISMATCH로 차단(요청 columns가 active buffer에 전무) + 그 후 성공한 show_display 없음 + finish 시도 + 1회 미시도
  //   본질: chain에서 신규 카테고리 질문(예: 재등록률 버퍼 후 "월별 매출")에 LLM이 call_api 없이 직전 버퍼를 switch_slot 재사용
  //         → show_display BUFFER MISMATCH → 회복(call_api) 없이 "버퍼랑 안 맞아서 못 보여줘"라며 내부사정 노출 + 답변 거부.
  //   GUARD 1(DISPLAY_REQUIRED)보다 먼저 평가 — GUARD 1은 잘못된 active buffer로 "show_display 하라" 도돌이를 유발하므로,
  //         BUFFER MISMATCH 상황에서는 "call_api로 올바른 데이터 조회" nudge가 우선이어야 함.
  //   범용: case-ID/한국어 키워드 hardcode 없음. _showDisplayBufferMismatchBlocked flag + show_display 성공 여부 = tool-path 구조 신호.
  //   bufferMismatchUnresolved는 caller가 (session._showDisplayBufferMismatchBlocked && !showDisplayResult)로 계산해 전달.
  if (bufferMismatchUnresolved && !bufferMismatchRetryAttempted) {
    return {
      action: 'retry',
      reason: FINISH_REASONS.BUFFER_MISMATCH_UNRESOLVED,
      retryGuide: {
        requestedColumns: Array.isArray(bufferMismatchInfo?.columns) ? bufferMismatchInfo.columns : [],
        activeSlot: bufferMismatchInfo?.activeSlot || null,
      },
    };
  }

  // ────────────────────────────────────────
  // GUARD 1 — DISPLAY REQUIRED
  // ────────────────────────────────────────
  //   [SCALAR-COMPUTE skip] compute 도구 호출 + show_display 미호출 시 → list 강제 X.
  //   본질: 사용자가 단일 집계값(평균/합계/최대/최소)을 원하면 LLM이 compute 호출 후 show_display 없이 finish.
  //         show_display도 부르면 list 의도 (compute + list 둘 다 원함) → DISPLAY_REQUIRED 적용.
  //   범용: user input keyword regex 0개. tool 호출 패턴(compute + show_display)만 사용 → 다국어/한국어 무관.
  //
  //   [BUFFER-MOD path 확장 — L20-COMPACT-NEWQUERY-001 #298 fix 2026-05-30]
  //   본질: chain turn에서 사용자가 buffer 가공 요청(컬럼 추가/필터/정렬/병합/파생 등) 시 LLM이
  //         customize_display/filter/sort/merge_slots/derive 만 호출 후 show_display 없이 finish 시도하는 FALSE_ACK 발생.
  //         예: T3 "생년월일 추가" → customize_display 만 호출 + "추가했어요" 안내 텍스트 + 표 미렌더링.
  //   해소: buffer-mod 도구가 호출됐는데 show_display 미호출 + buffer items 보유 + !meta 의도 → DISPLAY_REQUIRED retry.
  //         isListIntent regex (LIST_INTENT_KW) 의존 없이 tool-path 만으로 trigger — 짧은 chain input ("생년월일 추가", "여자만",
  //         "이름순" 등)이 LIST_INTENT_KW 매칭 실패해도 buffer-mod 의도 자체가 시각화 expectation의 구조 신호.
  //   범용: case-ID/specific input string hardcode X. tool 이름 enum 기반 (BUFFER_MOD_TOOLS).
  //         다국어/한국어 무관 — 구조 패턴(buffer 가공 → 시각화 expected).
  //   금지 예외: kpiValueMissingRetryAttempted/zeroCountAlternativeRetryAttempted 등 다른 retry 이미 발생 시도 시 skip (중복 retry 방지).
  const computeInvoked = toolsCalledThisTurn.includes('compute');
  const showDisplayInvoked = toolsCalledThisTurn.includes('show_display');
  // BUFFER_MOD_TOOLS — buffer 가공 도구 (시각화 expectation 동반)
  //   merge_slots/switch_slot 도 포함: chain context에서 슬롯 전환/병합 후 사용자는 결과 표를 기대.
  //   compute/derive 는 별도 — compute는 SCALAR-COMPUTE skip 룰, derive는 buffer 가공이지만 단독 호출 시 데이터 평탄화 의도라
  //     show_display 없이도 LLM이 추후 chain에서 표시 가능 → 본 GUARD에서 derive는 제외 (false positive 회피).
  const BUFFER_MOD_TOOLS = ['customize_display', 'filter', 'sort', 'merge_slots', 'switch_slot'];
  const bufferModInvoked = toolsCalledThisTurn.some(t => BUFFER_MOD_TOOLS.includes(t));
  if (
    !finishAllowedByGuard
    && itemsAvailable
    && !isCountIntent
    && !isMetaIntent
    && !(computeInvoked && !showDisplayInvoked)
    && (isListIntent || (bufferModInvoked && !showDisplayInvoked))
  ) {
    return {
      action: 'retry',
      reason: FINISH_REASONS.DISPLAY_REQUIRED,
      retryGuide: {
        itemCount: buffer.items.length,
        itemKeysHint: extractItemKeysHint(buffer),
        bufferModInvoked: bufferModInvoked && !showDisplayInvoked,
      },
    };
  }

  // ────────────────────────────────────────
  // GUARD 4 — NO_DATA_FETCH (P0-2 noTools 거짓 ack, 1회 한정)
  //   조건: 데이터 요청 의도(list|count) + call_api 미호출 + buffer empty + finalize 시도
  //   본질: LLM이 시각화 도구(show_*)가 데이터를 fetch한다고 오인 → call_api 없이 finalize
  //
  //   [GUIDE PATH BYPASS] — 2026-05-30 (HOWTO/FACT/ADVICE intent_mismatch P3)
  //   get_guide가 호출된 turn은 GUARD 4 우회. get_guide는 FACT/HOWTO/ADVICE 질문에
  //   대한 정당한 답변 fetch 경로이므로 call_api 없이 finish해도 "no data" retry가
  //   불필요. 강제 retry 시 LLM이 retry 메시지 "조회된 데이터가 없어요" 가이드를
  //   그대로 따라 prefix로 false ack 삽입하는 부작용 발생 (L33-GUIDE-FACT-002 Run 5 등).
  //   user input의 "보여줘/알려줘" 같은 list 키워드는 FACT/HOWTO에도 자연스럽게
  //   섞여있어 isListIntent만으로는 분별 불가 → tool path(get_guide 호출 여부)로 우회.
  //
  //   [INTENTIONAL EMPTY BYPASS] — 2026-05-31 (L30-CONCEPT-006 + L32-CHAIN-004 cluster)
  //   buffer가 empty인 이유가 (a) filter 적용 결과 0건 또는 (b) intersect/except merge 결과 0건이면
  //   "데이터 미fetch"가 아닌 INTENTIONAL EMPTY (조건 부합 entity 없음). 이 경우 LLM이 "0명/0건이야"
  //   같은 honest 응답을 finish하려는데 NO_DATA_FETCH retry로 막으면 LLM이 retry 메시지 "조회된
  //   데이터가 없어요" guide를 따라 잘못된 응답으로 회귀. 신호:
  //     - buffer.appliedFilters.length > 0 (filter 호출됨 — auto-apply 포함)
  //     - buffer.originalItems.length > 0 (filter 전 원본 데이터 존재)
  //     - merge_slots tool 호출됨 (intersect/except → 0건 merged)
  //     - 다른 slot에 데이터 존재 (sql_*/non-empty buffer 존재 — auto-chain SQL/intersect 흔적)
  //   case-ID/literal string 0 — 구조 신호만 사용 (다국어/한국어 무관).
  // ────────────────────────────────────────
  const appliedFiltersExist = Array.isArray(buffer?.appliedFilters) && buffer.appliedFilters.length > 0;
  const originalItemsExisted = Array.isArray(buffer?.originalItems) && buffer.originalItems.length > 0;
  const mergeSlotsInvoked = toolsCalledThisTurn.includes('merge_slots');
  const intentionalEmpty = bufferEmpty && (appliedFiltersExist || originalItemsExisted || mergeSlotsInvoked);

  if (
    !noDataFetchRetryAttempted &&
    (isListIntent || isCountIntent) &&
    !isMetaIntent &&
    bufferEmpty &&
    !intentionalEmpty &&
    !toolsCalledThisTurn.includes('call_api') &&
    !toolsCalledThisTurn.includes('get_guide') &&
    typeof rawAssistantText === 'string' &&
    rawAssistantText.trim().length > 0
  ) {
    return {
      action: 'retry',
      reason: FINISH_REASONS.NO_DATA_FETCH,
      retryGuide: {
        toolsCalled: toolsCalledThisTurn.slice(),
        isListIntent,
        isCountIntent,
      },
    };
  }

  // ────────────────────────────────────────
  // GUARD 13 — EMPTY BUFFER COUNT HALLUCINATION (L30-CONCEPT-006, 1회 한정)
  //   조건: bufferEmpty + intentionalEmpty (filter/merge로 0건) + rawAssistantText에 N>0 count 단언 + 1회 미시도
  //   본질: filter/intersect 결과 0건인데 LLM이 "1명이야" / "2건이야" 같은 fabricated count 응답.
  //         show_display 실패 후 LLM이 본인의 intro 텍스트 ("X명이야")를 finish response로 그대로 인용해서 발생.
  //   해소: "N명" / "N건" / "N개" / "N회" 패턴에서 N≥1인 경우 retry → "0명" / "조건에 맞는 결과 없어" 안내 유도.
  //   범용: case-ID/literal string hardcode X. 구조 신호(intentionalEmpty + 한국어 numeric count regex)만 사용.
  //         GUARD 3 NUMERIC_HALLUCINATION은 itemsAvailable=true에서만 fire — 본 가드는 그 반대 (bufferEmpty path) 보완.
  //   cap: numericHallucinationRetryCount === 0 공유 — GUARD 3과 동일 retry 카운터로 무한루프 차단.
  // ────────────────────────────────────────
  if (
    numericHallucinationRetryCount === 0 &&
    bufferEmpty &&
    intentionalEmpty &&
    typeof rawAssistantText === 'string' &&
    rawAssistantText.trim().length > 0
  ) {
    const EMPTY_COUNT_HALLUCINATION_RE = /(?:^|[^0-9])(\d+)\s*(명|건|개|회)(?:[이이야예에은는]|입|예요|이에요|있|되|있)/;
    const m = rawAssistantText.match(EMPTY_COUNT_HALLUCINATION_RE);
    if (m) {
      const claimedCount = parseInt(m[1], 10);
      if (Number.isFinite(claimedCount) && claimedCount >= 1) {
        return {
          action: 'retry',
          reason: FINISH_REASONS.EMPTY_BUFFER_COUNT_HALLUCINATION,
          retryGuide: {
            claimedCount,
            unit: m[2],
            sample: m[0].trim(),
          },
        };
      }
    }
  }

  // ────────────────────────────────────────
  // GUARD 2 — OUTRO RAW LEAK (1회 한정)
  // ────────────────────────────────────────
  if (!outroRetryAttempted && itemsAvailable && typeof detectors.detectRawNumericLeak === 'function') {
    const leak = detectors.detectRawNumericLeak(rawAssistantText, buffer);
    if (leak?.detected) {
      return {
        action: 'retry',
        reason: FINISH_REASONS.OUTRO_RAW_LEAK,
        retryGuide: {
          sample: leak.sample,
          changeFields: extractChangeFields(buffer),
        },
      };
    }
  }

  // ────────────────────────────────────────
  // GUARD 12 — COLUMN ENUM LEAK (1회 한정)
  //   조건: !columnEnumRetryAttempted + items + detectColumnEnumLeak detected
  //   본질: LLM이 outro에 resolvedColumnLabels 한국어 라벨 3+개를 직접 나열
  //         (예: "회원명, 회원권명, 홀딩 시작일, 홀딩 종료일, 홀딩 사유, 홀딩 잔여일")
  //         {{TABLE_COLS}} placeholder 가이드 무시 — retry로 깨끗한 응답 유도
  // ────────────────────────────────────────
  if (!columnEnumRetryAttempted && itemsAvailable && typeof detectors.detectColumnEnumLeak === 'function') {
    const enumLeak = detectors.detectColumnEnumLeak(rawAssistantText, buffer);
    if (enumLeak?.detected) {
      return {
        action: 'retry',
        reason: FINISH_REASONS.COLUMN_ENUM_LEAK,
        retryGuide: {
          sample: enumLeak.sample,
        },
      };
    }
  }

  // ────────────────────────────────────────
  // GUARD 5 — DETAIL FALLBACK MISLEADING (anchor 16, 1회 한정)
  //   조건: session._detailNotFoundContext 존재 (이전 DETAIL fail + specifier 감지)
  //       + finalText가 "찾을 수 없" / "없어요" / "총 1건" 같은 honor 표현 없음
  //       + 1회 미시도
  //   본질: LLM이 LIST fallback 후 전체 결과를 specifier 응답으로 finalize → user 오도 차단
  // ────────────────────────────────────────
  if (!detailFallbackRetryAttempted && detailNotFoundContext?.specifier) {
    const unfulfilled = typeof detectors.detectUnfulfilledSpecifier === 'function'
      ? detectors.detectUnfulfilledSpecifier(rawAssistantText, detailNotFoundContext)
      : null;
    if (unfulfilled?.detected) {
      return {
        action: 'retry',
        reason: FINISH_REASONS.DETAIL_FALLBACK_MISLEADING,
        retryGuide: {
          specifier: detailNotFoundContext.specifier,
          sample: unfulfilled.sample,
        },
      };
    }
  }

  // ────────────────────────────────────────
  // GUARD 7 — KPI VALUE MISSING ([1-A γ], 1회 한정)
  //   조건: kpiHintMeta 존재 (RATE_STATISTICS/RETENTION+RATE buffer 감지)
  //       + (strict 모드) kpiHintMeta.primaryRateKey 명시 시 → primary value 미포함 → retry
  //       + (legacy 모드) marker 없으면 summary의 *Rate suffix key 값이 finalText에 한 개도 포함 안 됨 → retry
  //       + 1회 미시도
  //   본질: RATE intention buffer 보유 상태에서 LLM이 finish 시 rate value 누락 + 잘못된 메트릭 인용 차단.
  //         strict 모드는 같은 intention 응답에 다중 rate field가 있을 때 (예: activeRate vs avgAttendanceRate)
  //         LLM이 보조 rate (오인 가능)만 인용하고 primary를 누락하는 패턴 차단.
  //         show_display path는 AgentSession placeholder swap이 보호하고, 본 GUARD는 finish() 직접 path를 보호.
  //   language-agnostic — 숫자값 검색만 사용 (key name/keyword 의존 X).
  //   주: show_display 호출 여부 무관 — KPI 값은 표 표시와 별개로 finalText에 포함 필요 (사용자 입장에서 명시적 KPI 응답 필수).
  // ────────────────────────────────────────
  if (!kpiValueMissingRetryAttempted && kpiHintMeta && kpiHintMeta.summary && typeof kpiHintMeta.summary === 'object') {
    const summary = kpiHintMeta.summary;
    const rateKeys = Object.keys(summary).filter(k => /[Rr]ate$/.test(k) && typeof summary[k] === 'number');
    if (rateKeys.length > 0 && typeof rawAssistantText === 'string') {
      const text = rawAssistantText;
      const primaryRateKey = typeof kpiHintMeta.primaryRateKey === 'string' && kpiHintMeta.primaryRateKey in summary
        ? kpiHintMeta.primaryRateKey
        : null;

      const isValueMissing = (key) => {
        const v = summary[key];
        const exact = String(v);
        const rounded = String(Math.round(v));
        // exact 또는 정수 rounded 값이 text 어디든 포함되면 hit (% 기호 등 별개 매칭)
        return !text.includes(exact) && !text.includes(rounded);
      };

      let shouldRetry = false;
      let primaryKey = rateKeys[0];

      if (primaryRateKey) {
        // strict 모드: primary value 미포함 시 retry (다른 rate value 포함 여부 무관)
        if (isValueMissing(primaryRateKey)) {
          shouldRetry = true;
          primaryKey = primaryRateKey;
        }
      }
      else {
        // legacy 모드: 모든 rate key 미포함 시 retry
        if (rateKeys.every(isValueMissing)) {
          shouldRetry = true;
          primaryKey = rateKeys[0];
        }
      }

      if (shouldRetry) {
        return {
          action: 'retry',
          reason: FINISH_REASONS.KPI_VALUE_MISSING,
          retryGuide: {
            slotName: kpiHintMeta.slotName,
            intention: kpiHintMeta.intention,
            key: primaryKey,
            value: summary[primaryKey],
            allRateKeys: rateKeys.map(k => `${k}=${summary[k]}`).join(', '),
            primaryRateKey: primaryRateKey || null,
          },
        };
      }
    }
  }

  // ────────────────────────────────────────
  // GUARD 11 — PERIOD COUNT MISMATCH ([1-N P26], 1회 한정)
  //   조건: periodCountMismatchMeta 존재 (mergeSlots에서 user input의 N월 enumeration vs call_api dateRange month set 비교)
  //       + missingMonths.length > 0
  //       + 1회 미시도
  //   본질: 사용자가 "1월 2월 3월 비교" 요청했는데 LLM이 stochastic하게 2 period만 호출 후 merge → 한 달 누락된 finalText 차단.
  //   GUARD 9(PERIOD_COUNTS_MISSING)는 merge된 슬롯의 count 명시 검증 — merge 전에 missing 슬롯이 있으면 GUARD 9가 잡지 못함 → 본 GUARD 11이 선행.
  //   language-agnostic — month set 비교만 (keyword/intention name 의존 X).
  // ────────────────────────────────────────
  if (!periodCountMismatchRetryAttempted && periodCountMismatchMeta?.missingMonths?.length > 0) {
    return {
      action: 'retry',
      reason: FINISH_REASONS.PERIOD_COUNT_MISMATCH,
      retryGuide: {
        inputMonths: periodCountMismatchMeta.inputMonths,
        calledMonths: periodCountMismatchMeta.calledMonths,
        missingMonths: periodCountMismatchMeta.missingMonths,
        sourceIntention: periodCountMismatchMeta.sourceIntention || null,
      },
    };
  }

  // ────────────────────────────────────────
  // GUARD 9 — PERIOD COUNTS MISSING ([1-F γ], 1회 한정)
  //   조건: periodCountHintMeta 존재 (merge_slots multi-period distinct dateRange 감지)
  //       + perPeriod 각 count 값 중 finalText에 1개라도 미포함
  //       + 1회 미시도
  //   본질: multi-period 비교에서 LLM이 outro에 per-period count를 부분 명시/누락/잘못된 값 사용 차단.
  //   language-agnostic — count 숫자값 검색만 (key name/keyword 의존 X).
  //   주: hint의 source가 summary.* 인 경우 더 critical (LLM이 _merged buffer의 derived "{period}_count"
  //       entity-aggregate field 혼동 가능성 높음).
  // ────────────────────────────────────────
  if (!periodCountsMissingRetryAttempted && periodCountHintMeta?.perPeriod?.length >= 2 && typeof rawAssistantText === 'string') {
    const text = rawAssistantText;
    const perPeriod = periodCountHintMeta.perPeriod;
    const missing = perPeriod.filter(p => {
      const v = p?.count;
      if (typeof v !== 'number' || !Number.isFinite(v)) return false;
      const exact = String(v);
      // count=0 인 케이스도 명시 필요 — "0건" 표현이 outro에 있어야 정상.
      // 단, text에 exact string이 있으면 hit (다른 숫자와 substring overlap 가능성은 false-positive 허용 — retry는 1회 한정이라 안전).
      return !text.includes(exact);
    });
    if (missing.length > 0) {
      return {
        action: 'retry',
        reason: FINISH_REASONS.PERIOD_COUNTS_MISSING,
        retryGuide: {
          missing: missing.map(p => ({ periodLabel: p.periodLabel, count: p.count, source: p.source })),
          allPeriods: perPeriod.map(p => ({ periodLabel: p.periodLabel, count: p.count })),
        },
      };
    }
  }

  // ────────────────────────────────────────
  // GUARD 8 — ZERO COUNT ALTERNATIVE UNTRIED ([1-E γ], 1회 한정)
  //   조건: zeroCountAlternativeMeta 존재 (call_api 2+ count=0 동일 intention 감지)
  //       + LLM이 same-category alternative intention 미시도 (toolsCalledThisTurn에 alternative 없음)
  //       + 1회 미시도
  //   본질: PRODUCT_SALES_LIST_BY+PERIOD 같은 niche intention 0건 시 LLM이 거짓 ack 차단.
  //   intention-agnostic — alternatives는 LOCAL_INTENTIONS_DATA에서 같은 카테고리 검색.
  // ────────────────────────────────────────
  if (!zeroCountAlternativeRetryAttempted && zeroCountAlternativeMeta?.alternatives?.length > 0) {
    const triedAlternatives = toolsCalledThisTurn.filter(t => zeroCountAlternativeMeta.alternatives.includes(t));
    if (triedAlternatives.length === 0) {
      return {
        action: 'retry',
        reason: FINISH_REASONS.ZERO_COUNT_ALTERNATIVE_UNTRIED,
        retryGuide: {
          intention: zeroCountAlternativeMeta.intention,
          count: zeroCountAlternativeMeta.count,
          alternatives: zeroCountAlternativeMeta.alternatives,
        },
      };
    }
  }

  // ────────────────────────────────────────
  // GUARD 10 — ENTITY_DETAIL_MISSING ([1-K], 1회 한정)
  //   조건: 소수 entity (items.length <= 3) + buffer items[0]에 NAME-like + detail field 4+ 보유 + response에 NAME value 포함(entity 응답 mode) + 다른 detail value < 50% mentioned + 1회 미시도
  //   본질: single-entity detail 응답에서 LLM이 detail 일부만 명시 → 사용자가 entity 식별만 가능, 디테일 누락
  //   언어 무관: userInputClassification (한국어 regex 의존) 제거. response에 NAME value 포함 여부로 entity 응답 mode 판정 → count/KPI 응답은 자동 skip
  // ────────────────────────────────────────
  if (
    !entityDetailMissingRetryAttempted
    && !finishAllowedByGuard
    && itemsAvailable
    && buffer.items.length <= 3
    && typeof rawAssistantText === 'string'
    && rawAssistantText.trim().length > 0
  ) {
    const sample = buffer.items[0];
    if (sample && typeof sample === 'object') {
      const NAME_FIELDS_LIST = ['storeName', 'name', 'memberName', 'memberShipName', 'membershipName', 'productName', 'itemName', 'lessonName', 'className', 'title'];
      const NAME_LIKE_RE = /name|title|label/i;
      // detail entries: string/number value + length > 3 + non-internal + non-ID-only field
      const detailEntries = Object.entries(sample).filter(([k, v]) => {
        if (k.startsWith('_')) return false;
        if (/Id$|_id$/.test(k)) return false;  // raw PK 제외 (storeId, partnerId 등)
        if (v == null || v === '') return false;
        if (typeof v === 'object') return false;  // nested object/array 제외
        const s = String(v);
        return s.length > 3;  // status (true/false), short flag 제외
      });
      if (detailEntries.length >= 4) {
        const nameEntries = detailEntries.filter(([k]) =>
          NAME_FIELDS_LIST.includes(k) || NAME_LIKE_RE.test(k)
        );
        if (nameEntries.length > 0) {
          // response에 NAME value 포함 여부 — entity 응답 mode 판정 (언어 무관)
          //   포함: entity 정보 응답 mode (이름 + 다른 detail 보여줄 의도)
          //   미포함: count/KPI/meta 응답 mode (skip)
          const responseHasNameValue = nameEntries.some(([, v]) =>
            rawAssistantText.includes(String(v))
          );
          if (responseHasNameValue) {
            // finish text에 각 detail value substring 포함 여부
            // [1-Q] mentioned 카운트 보강 — raw value substring OR label-with-colon 매칭
            //   LLM이 한글화 응답 ("성별: 여성") 시 backend raw enum 'female' substring 매칭 X → mentioned 부족 → GUARD false-positive retry → retry에서 LLM이 raw "female" degrade
            //   label-with-colon ("성별:") 매칭 추가 — LLM이 detail 명시한 의도 인식. 회귀 risk 낮음 — LLM short response (1줄 ack 등)는 label-colon 없음 → true-positive 보존
            const fieldLabels = buffer.availableFields || {};
            const mentioned = detailEntries.filter(([k, v]) => {
              if (rawAssistantText.includes(String(v))) return true;
              const label = fieldLabels[k];
              if (label && label !== k
                  && (rawAssistantText.includes(`${label}:`) || rawAssistantText.includes(`${label} :`))) {
                return true;
              }
              return false;
            }).length;
            const ratio = mentioned / detailEntries.length;
            if (ratio < 0.5) {
              return {
                action: 'retry',
                reason: FINISH_REASONS.ENTITY_DETAIL_MISSING,
                retryGuide: {
                  itemCount: buffer.items.length,
                  // [L10-DETAIL-SALES-001] retry guide value 사전 변환 — raw enum/format 노출 차단
                  //   gender male→남성, phone 11자리→포맷, birthDay 8자리→YYYY-MM-DD, profile URL→등록됨
                  //   변환 불가 field는 raw value 그대로 (truncate 적용)
                  detailFields: detailEntries.map(([k, v]) => ({
                    field: k,
                    value: String(_formatRetryGuideValue(k, v)).slice(0, 80),
                  })),
                  mentioned,
                  total: detailEntries.length,
                },
              };
            }
          }
        }
      }
    }
  }

  // ────────────────────────────────────────
  // GUARD 3 — NUMERIC HALLUCINATION (B1, 1회 한정)
  // ────────────────────────────────────────
  if (numericHallucinationRetryCount === 0 && itemsAvailable && typeof detectors.detectIntroOutroNumericHallucination === 'function') {
    const nh = detectors.detectIntroOutroNumericHallucination(rawAssistantText, buffer);
    if (nh?.detected) {
      return {
        action: 'retry',
        reason: FINISH_REASONS.NUMERIC_HALLUCINATION,
        retryGuide: { sample: nh.sample },
      };
    }
  }

  // ────────────────────────────────────────
  // PASS — finish 허용
  // ────────────────────────────────────────
  const transforms = [
    FINISH_TRANSFORMS.SANITIZE_POLITE,
    FINISH_TRANSFORMS.PLACEHOLDER_SUBSTITUTE,
    FINISH_TRANSFORMS.AUGMENT_CHANGE,
  ];
  if (numericHallucinationRetryCount >= 1) {
    transforms.push(FINISH_TRANSFORMS.STRIP_RAW_NUMBER_POST_RETRY);
  }
  return { action: 'finish', reason: FINISH_REASONS.OK, transforms };
}

/**
 * decision을 기반으로 LLM에 보낼 retry 메시지를 빌드. caller가 messages.push에 사용.
 * action !== 'retry' 시 null 반환.
 *
 * OL-5 — critical-reexamine + [RETRY N/MAX]:
 *   options.iteration = {current, max} 명시적 전달 시
 *   - 본문 앞에 `[RETRY N/MAX]` header prefix
 *   - 본문 뒤에 skeptical re-frame + trust harness suffix
 *   options.iteration 미지정 시 기존 본문 그대로 (backward compat).
 *
 * @param {FinishDecision} decision
 * @param {{iteration?: {current: number, max: number}}} [options]
 * @returns {string|null}
 */
export function buildRetryMessage(decision, options = {}) {
  if (!decision || decision.action !== 'retry') return null;
  const g = decision.retryGuide || {};

  let baseText = null;
  if (decision.reason === FINISH_REASONS.DISPLAY_REQUIRED) {
    const keyHint = g.itemKeysHint?.length ? g.itemKeysHint.join(', ') : '(no keys)';
    if (g.bufferModInvoked) {
      // BUFFER-MOD path 안내 — buffer 가공 도구만 호출 후 show_display 누락 시 (L20-COMPACT-NEWQUERY-001)
      baseText = `[FINISH BLOCKED — DISPLAY REQUIRED AFTER BUFFER MOD] Buffer has ${g.itemCount ?? '?'} items and you invoked buffer-modifying tool(s) this turn (customize_display/filter/sort/merge_slots/switch_slot) but did NOT call show_display. A short text acknowledgment like "추가했어요"/"적용했어요" is NOT the answer — the user expects the resulting table to be rendered. You MUST call show_display before finish.\n\nCall: show_display({ intro: "<short intro reflecting the buffer change>", columns: [<select 3-7 keys including the newly added/filtered/sorted columns>] })\nBuffer item keys: ${keyHint}`;
    } else {
      baseText = `[FINISH BLOCKED — DISPLAY REQUIRED] Buffer has ${g.itemCount ?? '?'} items and user input expects a list view. You MUST call show_display before finish.\n\nCall: show_display({ intro: "<short intro>", columns: [<select 3-6 keys>] })\nBuffer item keys: ${keyHint}`;
    }
  } else if (decision.reason === FINISH_REASONS.OUTRO_RAW_LEAK) {
    const fieldList = g.changeFields?.length ? g.changeFields.join(', ') : '(no change fields)';
    baseText = `[FINISH BLOCKED — OUTRO RAW NUMBER] response에 raw 숫자 "${g.sample ?? '?'}"가 포함됐습니다. placeholder ({{CHANGE:fieldName}} 또는 {{CHANGE_RATE:fieldName}})로 다시 작성해서 finish를 호출하세요. 가능한 fieldName: ${fieldList}.`;
  } else if (decision.reason === FINISH_REASONS.NUMERIC_HALLUCINATION) {
    baseText = `[FINISH BLOCKED — NUMERIC HALLUCINATION] response contains raw number "${g.sample ?? '?'}". You MUST use placeholders: {{SUM:field}}, {{AVG:field}}, {{COUNT}}, {{COUNT_NONZERO:field}}, {{MAX:field}}, {{MIN:field}}, {{SUMMARY:key}}. If no matching field/summary exists, OMIT the number entirely. Retry finish now.`;
  } else if (decision.reason === FINISH_REASONS.DETAIL_FALLBACK_MISLEADING) {
    baseText = `[FINISH BLOCKED — DETAIL FALLBACK MISLEADING] 사용자가 지정한 항목 "${g.specifier ?? '?'}"의 DETAIL 조회가 실패했고, LIST fallback 결과 전체를 specifier 응답으로 작성했습니다. 다음 중 하나로 다시 작성하세요:\n  (1) "${g.specifier ?? '?'}을(를) 찾을 수 없어요" 명시 (가장 권장).\n  (2) LIST 결과 중 "${g.specifier ?? '?'}"과 매칭되는 항목만 filter 후 그것만 응답 ("총 1건..." 형식).\n⚠ NEVER: 전체 LIST 결과를 specifier 응답으로 finalize.\n현재 응답 sample: "${g.sample ?? ''}"`;
  } else if (decision.reason === FINISH_REASONS.NO_DATA_FETCH) {
    const intent = g.isListIntent ? 'list' : g.isCountIntent ? 'count' : 'data';
    const toolsLine = g.toolsCalled?.length ? g.toolsCalled.join(', ') : '(none)';
    baseText = `[FINISH BLOCKED — NO DATA FETCH] User input expects ${intent} data but call_api was not invoked. Tools called this turn: ${toolsLine}. Visualization tools (show_*) display data only — they do NOT fetch. You MUST either:\n  (1) call call_api with appropriate intention to fetch data first, OR\n  (2) explicitly state no data was fetched (Korean: "조회된 데이터가 없어요").\nDo NOT claim to have shown data when buffer is empty. Retry finish after one of the above.`;
  } else if (decision.reason === FINISH_REASONS.EMPTY_RESPONSE_AFTER_VIZ) {
    baseText = `[FINISH BLOCKED — EMPTY RESPONSE] You called visualization tool "${g.vizTool ?? 'show_*'}" but invoked finish() with empty response. Visualization renders the chart/ranking, but the response text MUST include a short KPI summary (averages, totals, peak/lowest) so the user understands what they're looking at. Retry finish with response written naturally — e.g. "이번 달 평균 출석률은 X%예요. ..." — using the message hints from the visualization tool result.`;
  } else if (decision.reason === FINISH_REASONS.KPI_VALUE_MISSING) {
    if (g.primaryRateKey) {
      // strict 모드: primary rate key (intention 메타에서 명시) 값 누락 — 다른 rate 인용했어도 PRIMARY 누락 차단
      baseText = `[FINISH BLOCKED — PRIMARY KPI VALUE MISSING] Active KPI buffer "${g.slotName ?? '?'}" intention "${g.intention ?? '?'}" has PRIMARY rate key "${g.primaryRateKey}" = ${g.value ?? '?'}, but your finish() response did NOT include this value. Other rate keys may exist (${g.allRateKeys ?? '(none)'}) but THIS intention's headline metric is "${g.primaryRateKey}". Citing a secondary rate (e.g. avgAttendanceRate when activeRate is primary) misleads the user — both values may differ significantly. Retry finish with response naturally mentioning "${g.primaryRateKey}" value (${g.value ?? '?'}) as the headline metric in the user's input language. E.g. "전체 출석률은 ${g.value ?? '?'}%예요" / "Overall rate is ${g.value ?? '?'}%".`;
    }
    else {
      baseText = `[FINISH BLOCKED — KPI VALUE MISSING] Active KPI buffer "${g.slotName ?? '?'}" intention "${g.intention ?? '?'}" has rate key "${g.key ?? '?'}" = ${g.value ?? '?'}, but your finish() response did NOT include this value. RATE/RETENTION intentions exist to deliver this metric to the user — without it, the response leaves the user without the answer they asked for. All available rate keys: ${g.allRateKeys ?? '(none)'}. Retry finish with response naturally mentioning at least one rate value in the user's input language. E.g. "전체 출석률은 ${g.value ?? '?'}%예요" / "Overall rate is ${g.value ?? '?'}%".`;
    }
  } else if (decision.reason === FINISH_REASONS.ZERO_COUNT_ALTERNATIVE_UNTRIED) {
    const altList = g.alternatives?.length ? g.alternatives.join(', ') : '(none)';
    baseText = `[FINISH BLOCKED — ZERO COUNT ALTERNATIVE UNTRIED] Intention "${g.intention ?? '?'}" returned 0 items in ${g.count ?? '?'} consecutive calls this session, but you are finishing without trying alternative same-category intentions. Available alternatives: [${altList}]. User's request may match a different scope (e.g., "상품 판매" can mean physical PRODUCT merchandise OR overall store SALES including memberships). Call_api one of the alternatives FIRST to verify if data exists at the alternative scope. Only finish with "0건" acknowledgment if all alternatives also return 0건.`;
  } else if (decision.reason === FINISH_REASONS.ENTITY_DETAIL_MISSING) {
    const detailFormat = (g.detailFields || []).map(d => `  - ${d.field}: ${d.value}`).join('\n');
    baseText = `[FINISH BLOCKED — ENTITY DETAIL MISSING] Buffer has ${g.itemCount ?? '?'} small entity record(s) with ${g.total ?? '?'} detail fields, but your finish() response only mentions ${g.mentioned ?? '?'}/${g.total ?? '?'} field values. The user expects ALL detail (name, address, phone, hours, etc.) — short responses that only name the entity fail to deliver what was asked.\n\nYou MUST retry finish() with a response that mentions EVERY value below on separate lines, in the user's language (translate field labels naturally). The values below are ALREADY formatted for user display — quote them exactly as written (do NOT revert to raw enum/digits):\n${detailFormat}\n\nDo NOT skip any field. Do NOT add "원하시면 ..." re-proposal suffixes.`;
  } else if (decision.reason === FINISH_REASONS.PERIOD_COUNTS_MISSING) {
    const missingList = g.missing?.length
      ? g.missing.map(m => `${m.periodLabel}=${m.count}${m.source ? ` (${m.source})` : ''}`).join(', ')
      : '(none)';
    const allList = g.allPeriods?.length
      ? g.allPeriods.map(p => `${p.periodLabel}: ${p.count}`).join(' / ')
      : '(none)';
    baseText = `[FINISH BLOCKED — PERIOD COUNTS MISSING] Multi-period merge detected ${g.allPeriods?.length ?? '?'} distinct periods, but your finish() response is missing per-period count value(s): ${missingList}. Outro for multi-period comparison MUST mention each period count in user's input language — e.g. "${allList}". Do NOT cite the _merged buffer's derived "{period}_count" entity-aggregate field — use the authoritative per-slot source counts listed above. Retry finish with each period count explicitly stated.`;
  } else if (decision.reason === FINISH_REASONS.PERIOD_COUNT_MISMATCH) {
    const inputList = g.inputMonths?.length ? g.inputMonths.join(', ') : '(none)';
    const calledList = g.calledMonths?.length ? g.calledMonths.join(', ') : '(none)';
    const missingList = g.missingMonths?.length ? g.missingMonths.join(', ') : '(none)';
    const intentionHint = g.sourceIntention ? `intention="${g.sourceIntention}"` : 'the SAME intention used for the other periods';
    baseText = `[FINISH BLOCKED — PERIOD COUNT MISMATCH] User input enumerated ${g.inputMonths?.length ?? '?'} months [${inputList}] for comparison, but only ${g.calledMonths?.length ?? '?'} months [${calledList}] were actually loaded via call_api. Missing months: [${missingList}]. DO NOT finish with fewer periods than the user requested. Instead: (1) for EACH missing month, call call_api(${intentionHint}, startDate=<month's first day>, endDate=<month's last day>, slot="compare_<next index>"). (2) call merge_slots with ALL slots (existing + newly loaded). (3) call show_display with per-period columns. (4) THEN finish with all ${g.inputMonths?.length ?? '?'} periods covered. Skipping a period the user explicitly listed delivers incomplete comparison.`;
  }
  else if (decision.reason === FINISH_REASONS.COLUMN_ENUM_LEAK) {
    baseText = `[FINISH BLOCKED — COLUMN ENUM] response contains direct column name enumeration: "${g.sample ?? '?'}". NEVER list column labels (Korean or English) directly in outro/finish. The table already shows column headers — repeating them in text is redundant and noisy. Write a natural response WITHOUT mentioning column names. If you must describe table contents, use {{TABLE_COLS}} placeholder. Retry finish now.`;
  }
  else if (decision.reason === FINISH_REASONS.EMPTY_BUFFER_COUNT_HALLUCINATION) {
    baseText = `[FINISH BLOCKED — EMPTY BUFFER COUNT HALLUCINATION] Buffer is empty (filter/intersect 결과 0건) but your response claims "${g.sample ?? '?'}" (count=${g.claimedCount ?? '?'} ${g.unit ?? ''}). This is fabricated — no entity matched the user's conditions. Retry finish() honestly:\n  (1) "조건에 맞는 결과가 0${g.unit ?? '명'}이야" — explicit zero count, OR\n  (2) "조건에 맞는 [회원/항목]이 없어" — natural empty acknowledgment.\nDo NOT cite a non-zero number when the buffer has no rows.`;
  }
  else if (decision.reason === FINISH_REASONS.BUFFER_MISMATCH_UNRESOLVED) {
    const cols = g.requestedColumns?.length ? g.requestedColumns.join(', ') : '요청한 데이터';
    baseText = `[FINISH BLOCKED — BUFFER MISMATCH UNRESOLVED] The data the user asked for ([${cols}]) is NOT in the active buffer "${g.activeSlot ?? '?'}" — show_display was blocked (BUFFER MISMATCH) and you have NOT shown the requested data this turn. The active buffer holds a DIFFERENT dimension from a previous turn. Do NOT reuse the previous buffer via switch_slot. You MUST call get_intentions + call_api for an intention whose data matches the request, then show_display, then finish.\nNEVER expose internal state to the user (e.g. "버퍼랑 안 맞아서 못 보여줘" / "buffer mismatch" / "현재 버퍼에는 ...") and NEVER give up answering. Fetch the correct data and retry finish.`;
  }

  if (baseText === null) return null;
  return _wrapWithCriticalReexamine(baseText, options.iteration);
}

// ────────────────────────────────────────────────
// OL-5 — critical-reexamine + [RETRY N/MAX] wrapper
// ────────────────────────────────────────────────

export const CRITICAL_REEXAMINE_SUFFIX =
  'The system is questioning your completion claim. Critically re-examine each output from a skeptical perspective. Trust the harness, not your prior reasoning.';

function _wrapWithCriticalReexamine(text, iteration) {
  if (!iteration) return text;
  const current = typeof iteration.current === 'number' ? iteration.current : null;
  const max = typeof iteration.max === 'number' ? iteration.max : null;
  if (current === null || max === null) return text;
  return `[RETRY ${current}/${max}] ${text}\n\n${CRITICAL_REEXAMINE_SUFFIX}`;
}

export const _wrapWithCriticalReexamineForTest = _wrapWithCriticalReexamine;

// ────────────────────────────────────────────────
// internal helpers (export로 노출은 안 하지만 _ 접두로 테스트 가능)
// ────────────────────────────────────────────────

export function _extractItemKeysHint(buffer) {
  return extractItemKeysHint(buffer);
}

export function _extractChangeFields(buffer) {
  return extractChangeFields(buffer);
}

function extractItemKeysHint(buffer) {
  const first = buffer?.items?.[0];
  if (!first || typeof first !== 'object') return [];
  return Object.keys(first).filter((k) => !k.startsWith('_')).slice(0, ITEM_KEY_HINT_LIMIT);
}

function extractChangeFields(buffer) {
  const fields = new Set();
  for (const it of buffer?.items ?? []) {
    if (!it || typeof it !== 'object') continue;
    for (const k of Object.keys(it)) {
      // _change_X 추출. _changeRate_X는 _change_로 시작하지 않으므로 자연히 제외.
      if (k.startsWith('_change_')) {
        fields.add(k.slice('_change_'.length));
      }
    }
  }
  return [...fields];
}

/**
 * @typedef {object} FinishPolicyInput
 * @property {object|null} [buffer]                       — {items, summary, slots, ...}
 * @property {string} [rawAssistantText]                  — toolArgs.response
 * @property {{outroRetryAttempted?: boolean, numericHallucinationRetryCount?: number, noDataFetchRetryAttempted?: boolean, detailFallbackRetryAttempted?: boolean}} [retryCount]
 * @property {{finishAllowedByGuard?: boolean, detailNotFoundContext?: {specifier: string, intentionPrefix: string}}} [sessionFlags]
 * @property {{isListIntent?: boolean, isCountIntent?: boolean, isMetaIntent?: boolean}} [userInputClassification]
 * @property {{detectRawNumericLeak?: Function, detectIntroOutroNumericHallucination?: Function, detectUnfulfilledSpecifier?: Function}} [detectors]
 * @property {string[]} [toolsCalledThisTurn]             — P0-2 NO_DATA_FETCH guard용. 이번 turn에 호출된 도구 이름 목록
 */

/**
 * @typedef {object} FinishDecision
 * @property {'finish'|'retry'} action
 * @property {string} reason                              — FINISH_REASONS 중 하나
 * @property {object} [retryGuide]                        — action='retry' 시
 * @property {string[]} [transforms]                      — action='finish' 시
 */
